import React, { useEffect, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { ClipboardList, Save, Search, ChevronRight, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { handleGet, handlePost } from "../../../controllers/controller.all";
import SubmitButton from "../../../components/ui/SubmitButton";

export default function NotesBatchView() {
  const navigate = useNavigate();
  const user = useSelector(s => s.userReducer?.user);
  const token = user?.access_token || "";
  const ecole = user?.ecole;
  const ecoleId = ecole?._id || ecole || "";
  const profId = user?._id || "";

  const [classes, setClasses] = useState([]);
  const [matieres, setMatieres] = useState([]);
  const [periods, setPeriods] = useState([]);
  const [students, setStudents] = useState([]);

  const [selectedClasse, setSelectedClasse] = useState("");
  const [selectedMatiere, setSelectedMatiere] = useState("");
  const [selectedPeriode, setSelectedPeriode] = useState("");
  const [jour, setJour] = useState(new Date().toISOString().split("T")[0]);
  const [dateDevoir, setDateDevoir] = useState("");
  const [noteMax, setNoteMax] = useState("20");

  const [notes, setNotes] = useState({});   // student_id -> { valeur, appreciation, remarque }
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState(null);
  const [step, setStep] = useState(1); // 1: config, 2: saisie

  useEffect(() => {
    if (!token || !ecoleId) return;
    handleGet(`/classe?ecole=${ecoleId}`, res => setClasses(res?.classes || []), token);
    handleGet(`/matiere?ecole=${ecoleId}`, res => setMatieres(res?.matieres || []), token);
    handleGet(`/period?ecole=${ecoleId}`, res => setPeriods(res?.periods || []), token);
  }, [token, ecoleId]);

  const loadStudents = useCallback(async () => {
    if (!selectedClasse) return;
    setLoading(true);
    await handleGet(`/user/classe/${selectedClasse}`, res => {
      const eleves = (res?.users || []).filter(u => u.role === "ETUDIANT");
      setStudents(eleves);
      const initial = {};
      eleves.forEach(s => { initial[s._id] = { valeur: "", appreciation: "", remarque: "" }; });
      setNotes(initial);
    }, token);
    setLoading(false);
    setStep(2);
  }, [selectedClasse, token]);

  const updateNote = (studentId, field, value) => {
    setNotes(n => ({ ...n, [studentId]: { ...n[studentId], [field]: value } }));
  };

  const handleSave = async () => {
    const noteItems = students
      .filter(s => notes[s._id]?.valeur !== "")
      .map(s => ({
        user_id: s._id,
        valeur: notes[s._id]?.valeur || "0",
        note_max: noteMax,
        appreciation: notes[s._id]?.appreciation || "",
        remarque: notes[s._id]?.remarque || "",
        date_devoir: dateDevoir || jour,
      }));

    if (noteItems.length === 0) {
      alert("Aucune note à sauvegarder");
      return;
    }

    setSaving(true);
    setResult(null);
    await handlePost("/bulletin/notes/batch", {
      matiere_id: selectedMatiere,
      classe_id: selectedClasse,
      ecole_id: ecoleId,
      prof_id: profId,
      periode_id: selectedPeriode,
      jour,
      notes: noteItems,
    }, res => {
      setResult(res);
      if (res?.state === "success") {
        setTimeout(() => navigate("/notes"), 1500);
      }
    }, token);
    setSaving(false);
  };

  const filtered = students.filter(s => {
    const name = `${s.prenom || ""} ${s.nom || ""}`.toLowerCase();
    return name.includes(search.toLowerCase());
  });

  const filled = Object.values(notes).filter(n => n.valeur !== "").length;
  const total = students.length;

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <ClipboardList className="h-6 w-6 text-indigo-600" /> Saisie en lot de notes
        </h1>
        <p className="text-sm text-gray-500 mt-1">Saisissez les notes de toute une classe en une seule fois.</p>
      </div>

      {/* Step 1: Configuration */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
        <h2 className="text-sm font-semibold text-gray-700 mb-4">1. Configuration</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Classe *</label>
            <select value={selectedClasse} onChange={e => setSelectedClasse(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none">
              <option value="">-- Choisir --</option>
              {classes.map(c => <option key={c._id} value={c._id}>{c.libelle}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Matière *</label>
            <select value={selectedMatiere} onChange={e => setSelectedMatiere(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none">
              <option value="">-- Choisir --</option>
              {matieres.map(m => <option key={m._id} value={m._id}>{m.libelle}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Période</label>
            <select value={selectedPeriode} onChange={e => setSelectedPeriode(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none">
              <option value="">-- Choisir --</option>
              {periods.map(p => <option key={p._id} value={p._id}>{p.libelle}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Date de saisie</label>
            <input type="date" value={jour} onChange={e => setJour(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Date du devoir *</label>
            <input type="date" value={dateDevoir} onChange={e => setDateDevoir(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none" />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Note maximale</label>
            <select value={noteMax} onChange={e => setNoteMax(e.target.value)}
              className="w-full border border-gray-300 rounded-lg px-3 py-2.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none">
              {["10", "20", "100"].map(v => <option key={v} value={v}>/{v}</option>)}
            </select>
          </div>
        </div>
        <div className="mt-4">
          <button onClick={loadStudents} disabled={!selectedClasse || !selectedMatiere || !dateDevoir || loading}
            className="flex items-center gap-2 px-5 py-2.5 bg-indigo-600 text-white text-sm font-medium rounded-lg hover:bg-indigo-700 disabled:opacity-50 transition">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ChevronRight className="h-4 w-4" />}
            {loading ? "Chargement..." : "Charger les élèves"}
          </button>
        </div>
      </div>

      {/* Step 2: Note entry */}
      {step === 2 && students.length > 0 && (
        <>
          {/* Progress */}
          <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
            <div className="flex justify-between items-center mb-2 text-sm">
              <span className="text-gray-600">Progression : <strong>{filled}</strong> / {total} élève(s)</span>
              <span className="text-xs text-gray-400">{Math.round((filled/total)*100)}%</span>
            </div>
            <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-indigo-500 rounded-full transition-all" style={{ width: `${(filled/total)*100}%` }} />
            </div>
          </div>

          {/* Search */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Filtrer un élève..."
              className="w-full pl-9 pr-4 py-2.5 border border-gray-300 rounded-xl text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none" />
          </div>

          {/* Students */}
          <div className="flex flex-col gap-2">
            {filtered.map((student, idx) => {
              const n = notes[student._id] || {};
              const name = `${student.prenom || ""} ${student.nom || ""}`.trim();
              const filled = n.valeur !== "";
              return (
                <div key={student._id}
                  className={`bg-white rounded-xl border shadow-sm p-4 flex flex-col sm:flex-row sm:items-center gap-3 transition ${filled ? "border-indigo-200 bg-indigo-50/30" : "border-gray-200"}`}>
                  <div className="flex items-center gap-3 sm:w-48 flex-shrink-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold flex-shrink-0
                      ${filled ? "bg-indigo-100 text-indigo-700" : "bg-gray-100 text-gray-500"}`}>
                      {idx + 1}
                    </div>
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-gray-800 truncate">{name}</div>
                      <div className="text-xs text-gray-400 truncate">{student.numero_etudiant || "—"}</div>
                    </div>
                  </div>
                  <div className="flex flex-1 gap-2">
                    <div className="flex items-center gap-1 w-24 flex-shrink-0">
                      <input type="number" min="0" max={noteMax} step="0.5" placeholder="Note"
                        value={n.valeur} onChange={e => updateNote(student._id, "valeur", e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-2 py-2 text-sm text-center focus:ring-2 focus:ring-indigo-500 focus:outline-none" />
                      <span className="text-xs text-gray-400 whitespace-nowrap">/{noteMax}</span>
                    </div>
                    <input placeholder="Appréciation" value={n.appreciation}
                      onChange={e => updateNote(student._id, "appreciation", e.target.value)}
                      className="flex-1 border border-gray-300 rounded-lg px-2.5 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none" />
                    <input placeholder="Remarque (optionnel)" value={n.remarque}
                      onChange={e => updateNote(student._id, "remarque", e.target.value)}
                      className="flex-1 border border-gray-300 rounded-lg px-2.5 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:outline-none hidden lg:block" />
                  </div>
                </div>
              );
            })}
          </div>

          {/* Result toast */}
          {result && (
            <div className={`p-4 rounded-xl flex items-center gap-3 text-sm font-medium
              ${result.state === "success" ? "bg-emerald-50 border border-emerald-200 text-emerald-700" : "bg-amber-50 border border-amber-200 text-amber-700"}`}>
              {result.state === "success" ? <CheckCircle className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
              {result.message}
            </div>
          )}

          {/* Save button */}
          <div className="sticky bottom-4 mt-4">
            <SubmitButton 
              onClick={handleSave} 
              isSubmitting={saving} 
              disabled={filled === 0}
              className="w-full py-3 rounded-xl shadow-lg flex items-center justify-center gap-2"
              loadingText="Enregistrement..."
            >
              <Save className="h-5 w-5" /> Enregistrer {filled} note(s)
            </SubmitButton>
          </div>
        </>
      )}
    </div>
  );
}
